import axios, { AxiosError } from 'axios';
import 'chartjs-plugin-datalabels';
import React from 'react';
import { Alert, Button, ControlLabel, Form, FormControl } from 'react-bootstrap';
import { Loader } from '../../../components/Loader';
import {
  ChartDefinitionAndData,
  ChartGroupDefinition,
  PageChart,
} from '../../InsightsTypes';
import ErrorBoundary from './ErrorBoundary';
import InnerChartComponent from './InnerChartComponent';
import InnerChartConfigurationComponent from './InnerChartConfigurationComponent';
import { getBongoUrl } from '../../../common/utils/bongo';

// This chart otherwise returns every conversion type across the account, which
// on high-volume tenants is millions of rows and times the request out
// (CPF-2405). For it alone we scope the data to a single conversion model
// server-side: pick a model first, then load only its rows. The bongo side only
// honours ?conversion_model for this chart id and behind a flag, so unfiltered
// stays the fallback.
const CONVERSION_FILTERED_CHARTS = [
  'customer_fit_conversions',
  'behavioral_conversions',
  'lead_grade_conversions',
];
// Default the initial selection to Closed Won when it's available.
const DEFAULT_CONVERSION_MODEL = 'Closed Won';

type ChartComponentProps = {
  tenant: number;
  chart: PageChart;
  superKudu: boolean;
};

type ChartComponentState = {
  charts: ChartDefinitionAndData[];
  error: boolean;
  errorMessage: string;
  configurationMode: boolean;
  conversionModels: string[];
  selectedConversionModel: string | null;
};

export default class ChartComponent extends React.Component<
  ChartComponentProps,
  ChartComponentState
> {
  constructor(props: ChartComponentProps) {
    super(props);
    this.loadChart = this.loadChart.bind(this);
    this.changeConversionModel = this.changeConversionModel.bind(this);
    this.setConfigurationMode = this.setConfigurationMode.bind(this);
    this.cancelConfigurationModeAndRefresh = this.cancelConfigurationModeAndRefresh.bind(
      this
    );
    this.state = {
      charts: [],
      error: false,
      errorMessage: '',
      configurationMode: false,
      conversionModels: [],
      selectedConversionModel: null,
    };
  }

  async componentDidMount() {
    if (this.isConversionFiltered) {
      // eslint-disable-next-line
      this.loadConversionModelsAndChart();
    } else {
      // eslint-disable-next-line
      this.loadChart();
    }
  }

  get isConversionFiltered(): boolean {
    return CONVERSION_FILTERED_CHARTS.includes(this.props.chart.name);
  }

  async loadConversionModelsAndChart() {
    try {
      const { tenant } = this.props;
      const url = `${getBongoUrl()}/v1/org/${tenant}/data/mappings/conversion`;
      const { data } = await axios.get(url);
      // Bongo returns a serialized ConversionMappingManager, so the groups live
      // at data.data.conversionTypesGroups (not the top level).
      const groups: Array<{ name?: string; type?: string }> =
        data?.data?.conversionTypesGroups ?? [];
      // Mirror exactly what /mapping/conversion_mapping shows: every group's
      // name, plus the default SQO / Closed Won / Open Opp groups that page
      // injects for display when a group of that type isn't already present
      // (prepareConversionMappingDataDisplay).
      const names: string[] = groups
        .map((group) => group.name)
        .filter(Boolean) as string[];
      const presentTypes = groups.map((group) => group.type);
      ['SQO', 'Closed Won', 'Open Opp'].forEach((defaultType) => {
        if (!presentTypes.includes(defaultType)) {
          names.push(defaultType);
        }
      });
      const conversionModels: string[] = [...new Set(names)];
      const selectedConversionModel = conversionModels.includes(
        DEFAULT_CONVERSION_MODEL
      )
        ? DEFAULT_CONVERSION_MODEL
        : conversionModels[0] ?? null;
      this.setState({ conversionModels, selectedConversionModel });
      await this.loadChart(selectedConversionModel);
    } catch (err) {
      this.setState({
        error: true,
        errorMessage:
          'Could not load the list of conversion types for this chart.',
      });
    }
  }

  async loadChart(conversionModel?: string | null) {
    try {
      const { tenant, chart } = this.props;
      const { name, ids } = chart;
      const filter = conversionModel
        ? `?conversion_model=${encodeURIComponent(conversionModel)}`
        : '';
      const url = `${getBongoUrl()}/v1/org/${tenant}/insights/chart/${name}${filter}`;
      const { data } = await axios.get(url).catch((err: AxiosError) => {
        if (err.response?.status === 404) {
          throw new Error('No data was found for this chart');
        }
        if (err.response?.status === 500) {
          throw new Error('A server error occured while loading the chart');
        }
        throw new Error('Cannot load this chart');
      });
      const charts: ChartDefinitionAndData[] = data.chart
        .filter((definition: ChartGroupDefinition) =>
          ids.includes(definition.id)
        )
        .map((definition: ChartGroupDefinition) => {
          return {
            definition,
            data: data.data,
          };
        });
      this.setState({
        charts,
      });
    } catch (err) {
      this.setState({
        error: true,
        errorMessage:
          err.message ?? 'An unexpected error occured while loading the chart.',
      });
    }
  }

  async changeConversionModel(newModel: string) {
    // Reload the table for the newly selected conversion model (rows are
    // server-filtered, not filtered client-side), so clear the current rows to
    // show the loader.
    this.setState({
      selectedConversionModel: newModel,
      charts: [],
      error: false,
    });
    await this.loadChart(newModel);
  }

  setConfigurationMode(action: 'enable' | 'disable') {
    this.setState({
      configurationMode: action === 'enable',
    });
  }

  async cancelConfigurationModeAndRefresh() {
    this.setState({
      configurationMode: false,
    });
  }

  render() {
    const {
      tenant,
      chart: { align, name },
    } = this.props;
    const {
      charts,
      error,
      errorMessage,
      configurationMode,
      conversionModels,
      selectedConversionModel,
    } = this.state;

    const alertBlock = (
      <Alert bsStyle="info" className="mt-4">
        {errorMessage}
      </Alert>
    );

    if (error) {
      return alertBlock;
    }

    // Only take over the filtering (and hide the client-side dropdown) once we
    // actually have a model list; otherwise fall back to the normal chart so we
    // never render a chart with no filter at all.
    const serverFilterActive =
      this.isConversionFiltered && conversionModels.length > 0;

    const conversionModelSelector =
      this.isConversionFiltered && conversionModels.length > 0 ? (
        <Form inline className="mt-4 mb-2">
          <ControlLabel className="mr-2">Conversion Type</ControlLabel>
          <FormControl
            componentClass="select"
            value={selectedConversionModel ?? ''}
            onChange={(e: any) => this.changeConversionModel(e.target.value)}
          >
            {conversionModels.map((model, i) => {
              return (
                <option key={`conversion_model_${i}`} value={model}>
                  {model}
                </option>
              );
            })}
          </FormControl>
        </Form>
      ) : null;

    if (!charts.length) {
      return (
        <ErrorBoundary alertBlock={alertBlock}>
          {conversionModelSelector}
          <Loader />
        </ErrorBoundary>
      );
    }

    return (
      <ErrorBoundary alertBlock={alertBlock}>
        {conversionModelSelector}
        <div
          className={`row  ${
            configurationMode || align === 'horizontal'
              ? 'justify-content-center'
              : ''
          }`}
        >
          {charts.map((chart) => {
            return (
              <div
                className={`
                  ${align === 'horizontal' ? 'col-sm-8' : 'col-sm-10 offset-1'}
                  ${configurationMode ? 'd-flex justify-content-center' : ''}
                `}
                key={`chart_${chart.definition.id}`}
              >
                <div
                  className={`card box-shadow-3d mt-4 ${
                    configurationMode ? 'w-50' : ''
                  }`}
                >
                  <div className="card-header my-auto">
                    <div className="row justify-content-between">
                      <div className="col my-auto">
                        <h5 className="mb-0">
                          {chart.definition.title}
                          {configurationMode ? ' - Customize' : ''}
                        </h5>
                      </div>
                      {!configurationMode &&
                        chart.definition.charts.some((c) =>
                          c?.properties?.some((p) => p.customizable)
                        ) && (
                          <div className="col-auto">
                            <Button
                              className="float-right btn-outline-secondary"
                              onClick={() =>
                                this.setConfigurationMode('enable')
                              }
                            >
                              Customize
                            </Button>
                          </div>
                        )}
                    </div>
                  </div>
                  <div className="card-body">
                    {configurationMode ? (
                      <InnerChartConfigurationComponent
                        tenant={tenant}
                        chartName={name}
                        chartDefinition={chart.definition}
                        cancelConfigurationMode={() =>
                          this.setConfigurationMode('disable')
                        }
                        cancelConfigurationModeAndRefresh={
                          this.cancelConfigurationModeAndRefresh
                        }
                      />
                    ) : (
                      <InnerChartComponent
                        chartDefinitionAndData={chart}
                        serverFiltered={serverFilterActive}
                      />
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </ErrorBoundary>
    );
  }
}
